// src/pages/LayoutEditor.jsx

import React, {
    useEffect,
    useRef,
    useState
} from "react";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import {
    Save,
    Trash2,
    RotateCw,
    Plus,
    ArrowLeft
} from "lucide-react";

import api from "../services/api";


// =========================================================
// CONFIG
// =========================================================

const GRID_SIZE = 20;

const DEFAULT_SLOT_WIDTH = 80;

const DEFAULT_SLOT_HEIGHT = 35;

const PARKING_AREA = {
    x: 220,
    y: 80,
    width: 960,
    height: 640
};


// =========================================================
// HELPERS
// =========================================================

const snapToGrid = (value) => {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
};

const generateSlotCode = (index) => {
    return `P-${String(index + 1).padStart(3, "0")}`;
};

const rotatePoint = (
    cx,
    cy,
    x,
    y,
    angle
) => {

    const radians = (Math.PI / 180) * angle;

    const cos = Math.cos(radians);

    const sin = Math.sin(radians);

    const nx =
        cos * (x - cx) -
        sin * (y - cy) +
        cx;

    const ny =
        sin * (x - cx) +
        cos * (y - cy) +
        cy;

    return {
        x: nx,
        y: ny
    };
};

const buildPolygon = (
    centerX,
    centerY,
    width,
    height,
    rotation
) => {

    const halfW = width / 2;

    const halfH = height / 2;

    const points = [

        {
            x: centerX - halfW,
            y: centerY - halfH
        },

        {
            x: centerX + halfW,
            y: centerY - halfH
        },

        {
            x: centerX + halfW,
            y: centerY + halfH
        },

        {
            x: centerX - halfW,
            y: centerY + halfH
        }
    ];

    return points.map((p) =>
        rotatePoint(
            centerX,
            centerY,
            p.x,
            p.y,
            rotation
        )
    );
};

const clampSlotPosition = (
    centerX,
    centerY,
    width,
    height
) => {
    const halfW = width / 2;
    const halfH = height / 2;

    const minX = PARKING_AREA.x + halfW;
    const maxX = PARKING_AREA.x + PARKING_AREA.width - halfW;
    const minY = PARKING_AREA.y + halfH;
    const maxY = PARKING_AREA.y + PARKING_AREA.height - halfH;

    return {
        x: Math.min(
            Math.max(centerX, minX),
            maxX
        ),
        y: Math.min(
            Math.max(centerY, minY),
            maxY
        )
    };
};

const clampSlotToArea = (slot) => {
    const { x, y } = clampSlotPosition(
        slot.slot_center_x,
        slot.slot_center_y,
        slot.width,
        slot.height
    );

    return {
        ...slot,
        slot_center_x: x,
        slot_center_y: y,
        polygon_points: buildPolygon(
            x,
            y,
            slot.width,
            slot.height,
            slot.rotation
        )
    };
};

const clampSlotDimensions = (width, height) => {
    const MIN_WIDTH = 20;
    const MAX_WIDTH = 300;
    const MIN_HEIGHT = 20;
    const MAX_HEIGHT = 300;

    return {
        width: Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width)),
        height: Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height))
    };
};


// =========================================================
// COMPONENT
// =========================================================

export default function LayoutEditor() {

    const navigate = useNavigate();

    const { facilityId } = useParams();

    const svgRef = useRef(null);

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [slots, setSlots] =
        useState([]);

    const [selectedSlotId, setSelectedSlotId] =
        useState(null);

    const [dragging, setDragging] =
        useState(false);

    const [dragOffset, setDragOffset] =
        useState({
            x: 0,
            y: 0
        });

    const [canvasSize] = useState({
        width: 1400,
        height: 800
    });


    // =======================================================
    // LOAD LAYOUT
    // =======================================================

    useEffect(() => {

        loadLayout();

    }, []);


    const loadLayout = async () => {

        try {

            setLoading(true);

            const response =
                await api.get(
                    `/admin/facility/${facilityId}/layout`
                );

            const backendSlots =
                response.data?.slots || [];

            const normalized =
                backendSlots.map((slot) => {
                    const sourceWidth =
                        slot.width ||
                        DEFAULT_SLOT_WIDTH;

                    const sourceHeight =
                        slot.height ||
                        DEFAULT_SLOT_HEIGHT;

                    return clampSlotToArea({
                        ...slot,
                        polygon_points:
                            slot.polygon_points || [],
                        width: sourceWidth,
                        height: sourceHeight,
                        rotation: slot.rotation || 0,
                        slot_center_x:
                            slot.slot_center_x ||
                            PARKING_AREA.x +
                                sourceWidth / 2,
                        slot_center_y:
                            slot.slot_center_y ||
                            PARKING_AREA.y +
                                sourceHeight / 2
                    });
                });

            setSlots(normalized);

        } catch (error) {

            console.error(
                "Layout load failed:",
                error
            );

        } finally {

            setLoading(false);
        }
    };


    // =======================================================
    // CREATE SLOT
    // =======================================================

    const createSlot = () => {

        const SLOT_GAP_X = 140;
        const SLOT_GAP_Y = 80;

        const START_X = 200;
        const START_Y = 100;

        const MAX_COLUMNS = 10;

        const index = slots.length;

        const row = Math.floor(index / MAX_COLUMNS);

        const col = index % MAX_COLUMNS;

        const centerX =
            START_X + col * SLOT_GAP_X;

        const centerY =
            START_Y + row * SLOT_GAP_Y;

        const width =
            DEFAULT_SLOT_WIDTH;

        const height =
            DEFAULT_SLOT_HEIGHT;

        const clamped = clampSlotPosition(
            centerX,
            centerY,
            width,
            height
        );

        const rotation = 0;

        const polygon =
            buildPolygon(
                clamped.x,
                clamped.y,
                width,
                height,
                rotation
            );

        const newSlot = {

            slot_id:
                Date.now(),

            slot_code:
                generateSlotCode(
                    slots.length
                ),

            slot_name: "",

            zone_name: "ZONE-A",

            floor_number: 0,

            slot_type: "CAR",

            slot_center_x:
                clamped.x,

            slot_center_y:
                clamped.y,

            width,

            height,

            rotation,

            polygon_points:
                polygon,

            camera_id: "",

            metadata_json: {},

            is_active: true
        };

        setSlots((prev) => [
            ...prev,
            newSlot
        ]);
    };


    // =======================================================
    // SELECT SLOT
    // =======================================================

    const handleSelectSlot = (
        slotId
    ) => {

        setSelectedSlotId(slotId);
    };


    // =======================================================
    // DELETE SLOT
    // =======================================================

    const deleteSelectedSlot = () => {

        if (!selectedSlotId)
            return;

        setSlots((prev) =>
            prev.filter(
                (s) =>
                    s.slot_id !==
                    selectedSlotId
            )
        );

        setSelectedSlotId(null);
    };


    // =======================================================
    // ROTATE SLOT
    // =======================================================

    const rotateSelectedSlot = () => {

        if (!selectedSlotId)
            return;

        setSlots((prev) =>
            prev.map((slot) => {

                if (
                    slot.slot_id !==
                    selectedSlotId
                ) {
                    return slot;
                }

                const rotation =
                    (slot.rotation || 0) + 15;

                return {

                    ...slot,

                    rotation,

                    polygon_points:
                        buildPolygon(
                            slot.slot_center_x,
                            slot.slot_center_y,
                            slot.width,
                            slot.height,
                            rotation
                        )
                };
            })
        );
    };


    // =======================================================
    // UPDATE SLOT SIZE
    // =======================================================

    const updateSlotSize = (newWidth, newHeight) => {

        if (!selectedSlotId)
            return;

        const clamped = clampSlotDimensions(newWidth, newHeight);

        setSlots((prev) =>
            prev.map((slot) => {

                if (
                    slot.slot_id !==
                    selectedSlotId
                ) {
                    return slot;
                }

                const repositioned = clampSlotPosition(
                    slot.slot_center_x,
                    slot.slot_center_y,
                    clamped.width,
                    clamped.height
                );

                return {
                    ...slot,
                    width: clamped.width,
                    height: clamped.height,
                    slot_center_x: repositioned.x,
                    slot_center_y: repositioned.y,
                    polygon_points: buildPolygon(
                        repositioned.x,
                        repositioned.y,
                        clamped.width,
                        clamped.height,
                        slot.rotation
                    )
                };
            })
        );
    };

    const getSelectedSlot = () => {
        return slots.find(
            (s) => s.slot_id === selectedSlotId
        );
    };


    // =======================================================
    // MOUSE DOWN
    // =======================================================

    const handleMouseDown = (
        e,
        slot
    ) => {

        e.stopPropagation();

        const svg =
            svgRef.current;

        const rect =
            svg.getBoundingClientRect();

        const mouseX =
            e.clientX - rect.left;

        const mouseY =
            e.clientY - rect.top;

        setSelectedSlotId(
            slot.slot_id
        );

        setDragging(true);

        setDragOffset({

            x:
                mouseX -
                slot.slot_center_x,

            y:
                mouseY -
                slot.slot_center_y
        });
    };


    // =======================================================
    // MOUSE MOVE
    // =======================================================

    const handleMouseMove = (
        e
    ) => {

        if (
            !dragging ||
            !selectedSlotId
        ) {
            return;
        }

        const svg =
            svgRef.current;

        const rect =
            svg.getBoundingClientRect();

        const mouseX =
            e.clientX - rect.left;

        const mouseY =
            e.clientY - rect.top;

        const centerX =
            snapToGrid(
                mouseX -
                dragOffset.x
            );

        const centerY =
            snapToGrid(
                mouseY -
                dragOffset.y
            );

        setSlots((prev) =>
            prev.map((slot) => {

                if (
                    slot.slot_id !==
                    selectedSlotId
                ) {
                    return slot;
                }

                const clamped = clampSlotPosition(
                    centerX,
                    centerY,
                    slot.width,
                    slot.height
                );

                return {

                    ...slot,

                    slot_center_x:
                        clamped.x,

                    slot_center_y:
                        clamped.y,

                    polygon_points:
                        buildPolygon(
                            clamped.x,
                            clamped.y,
                            slot.width,
                            slot.height,
                            slot.rotation
                        )
                };
            })
        );
    };


    // =======================================================
    // MOUSE UP
    // =======================================================

    const handleMouseUp = () => {

        setDragging(false);
    };


    // =======================================================
    // SAVE LAYOUT
    // =======================================================

    const saveLayout = async () => {

        try {

            setSaving(true);

            const payload = {

                facility_key:
                    Number(facilityId),

                slots:
                    slots.map((slot) => ({

                        slot_code:
                            slot.slot_code,

                        slot_name:
                            slot.slot_name,

                        zone_name:
                            slot.zone_name,

                        floor_number:
                            slot.floor_number,

                        slot_type:
                            slot.slot_type,

                        polygon_points:
                            slot.polygon_points,

                        slot_center_x:
                            slot.slot_center_x,

                        slot_center_y:
                            slot.slot_center_y,

                        width:
                            slot.width,

                        height:
                            slot.height,

                        rotation:
                            slot.rotation,

                        camera_id:
                            slot.camera_id,

                        metadata_json:
                            slot.metadata_json,

                        is_active:
                            slot.is_active
                    }))
            };

            await api.post(
                `/admin/facility/${facilityId}/layout`,
                payload
            );

            alert(
                "Layout saved successfully"
            );

        } catch (error) {

            console.error(
                "Save layout failed:",
                error
            );

            alert(
                "Failed to save layout"
            );

        } finally {

            setSaving(false);
        }
    };


    // =======================================================
    // RENDER GRID
    // =======================================================

    const renderGrid = () => {

        const lines = [];

        for (
            let x = 0;
            x <= canvasSize.width;
            x += GRID_SIZE
        ) {

            lines.push(

                <line
                    key={`v-${x}`}
                    x1={x}
                    y1={0}
                    x2={x}
                    y2={canvasSize.height}
                    stroke="#2b2b2b"
                    strokeWidth="1"
                />
            );
        }

        for (
            let y = 0;
            y <= canvasSize.height;
            y += GRID_SIZE
        ) {

            lines.push(

                <line
                    key={`h-${y}`}
                    x1={0}
                    y1={y}
                    x2={canvasSize.width}
                    y2={y}
                    stroke="#2b2b2b"
                    strokeWidth="1"
                />
            );
        }

        return lines;
    };


    // =======================================================
    // LOADING
    // =======================================================

    if (loading) {

        return (

            <div
                className="
          w-full
          h-screen
          flex
          items-center
          justify-center
          bg-black
          text-white
        "
            >
                Loading Layout...
            </div>
        );
    }


    // =======================================================
    // UI
    // =======================================================

    return (

        <div
            className="
        w-full
        h-screen
        flex
        flex-col
        bg-[#111111]
        text-white
      "
        >

            {/* ================================================= */}
            {/* TOPBAR */}
            {/* ================================================= */}

            <div
                className="
          h-[70px]
          border-b
          border-[#2a2a2a]
          flex
          items-center
          justify-between
          px-6
          shrink-0
        "
            >

                <div
                    className="
            flex
            items-center
            gap-3
          "
                >

                    <button
                        onClick={() =>
                            navigate(-1)
                        }
                        className="
              p-2
              rounded
              bg-[#1d1d1d]
              hover:bg-[#2b2b2b]
            "
                    >
                        <ArrowLeft size={18} />
                    </button>

                    <h1
                        className="
              text-xl
              font-semibold
            "
                    >
                        Layout Editor
                    </h1>

                </div>

                <div
                    className="
            flex
            items-center
            gap-3
          "
                >

                    <button
                        onClick={createSlot}
                        className="
              flex
              items-center
              gap-2
              bg-blue-600
              hover:bg-blue-700
              px-4
              py-2
              rounded
            "
                    >
                        <Plus size={16} />
                        Add Slot
                    </button>

                    {selectedSlotId && (
                        <>
                            <input
                                type="number"
                                value={getSelectedSlot()?.width || ''}
                                onChange={(e) => {
                                    const w = parseInt(e.target.value) || DEFAULT_SLOT_WIDTH;
                                    const h = getSelectedSlot()?.height || DEFAULT_SLOT_HEIGHT;
                                    updateSlotSize(w, h);
                                }}
                                placeholder="Width"
                                min="20"
                                max="300"
                                className="
                  w-20
                  px-2
                  py-2
                  rounded
                  bg-[#1d1d1d]
                  border
                  border-[#2a2a2a]
                  text-white
                  text-sm
                  focus:outline-none
                  focus:border-blue-500
                "
                            />

                            <input
                                type="number"
                                value={getSelectedSlot()?.height || ''}
                                onChange={(e) => {
                                    const h = parseInt(e.target.value) || DEFAULT_SLOT_HEIGHT;
                                    const w = getSelectedSlot()?.width || DEFAULT_SLOT_WIDTH;
                                    updateSlotSize(w, h);
                                }}
                                placeholder="Height"
                                min="20"
                                max="300"
                                className="
                  w-20
                  px-2
                  py-2
                  rounded
                  bg-[#1d1d1d]
                  border
                  border-[#2a2a2a]
                  text-white
                  text-sm
                  focus:outline-none
                  focus:border-blue-500
                "
                            />
                        </>
                    )}

                    <button
                        onClick={
                            rotateSelectedSlot
                        }
                        disabled={
                            !selectedSlotId
                        }
                        className="
              flex
              items-center
              gap-2
              bg-[#1d1d1d]
              hover:bg-[#2a2a2a]
              px-4
              py-2
              rounded
              disabled:opacity-50
            "
                    >
                        <RotateCw size={16} />
                        Rotate
                    </button>

                    <button
                        onClick={
                            deleteSelectedSlot
                        }
                        disabled={
                            !selectedSlotId
                        }
                        className="
              flex
              items-center
              gap-2
              bg-red-600
              hover:bg-red-700
              px-4
              py-2
              rounded
              disabled:opacity-50
            "
                    >
                        <Trash2 size={16} />
                        Delete
                    </button>

                    <button
                        onClick={saveLayout}
                        disabled={saving}
                        className="
              flex
              items-center
              gap-2
              bg-green-600
              hover:bg-green-700
              px-5
              py-2
              rounded
              font-medium
            "
                    >
                        <Save size={16} />

                        {
                            saving
                                ? "Saving..."
                                : "Save Layout"
                        }
                    </button>

                </div>

            </div>


            {/* ================================================= */}
            {/* CANVAS */}
            {/* ================================================= */}

            <div
                className="
          flex-1
          min-h-0
          overflow-hidden
          bg-[#0b0b0b]
        "
            >

                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${canvasSize.width} ${canvasSize.height}`}
                    preserveAspectRatio="xMidYMid meet"
                    className="w-full h-full"
                    onMouseMove={
                        handleMouseMove
                    }
                    onMouseUp={
                        handleMouseUp
                    }
                    onMouseLeave={
                        handleMouseUp
                    }
                >

                    <rect
                        x={PARKING_AREA.x}
                        y={PARKING_AREA.y}
                        width={PARKING_AREA.width}
                        height={PARKING_AREA.height}
                        rx="36"
                        fill="#e2e8f0"
                        stroke="#94a3b8"
                        strokeWidth="4"
                    />

                    <line
                        x1={PARKING_AREA.x + 20}
                        y1={PARKING_AREA.y + 320}
                        x2={PARKING_AREA.x + PARKING_AREA.width - 20}
                        y2={PARKING_AREA.y + 320}
                        stroke="#94a3b8"
                        strokeWidth="14"
                        strokeDasharray="30 20"
                    />

                    <line
                        x1={PARKING_AREA.x + 480}
                        y1={PARKING_AREA.y + 20}
                        x2={PARKING_AREA.x + 480}
                        y2={PARKING_AREA.y + PARKING_AREA.height - 20}
                        stroke="#94a3b8"
                        strokeWidth="14"
                        strokeDasharray="30 20"
                    />

                    <text
                        x={PARKING_AREA.x + 20}
                        y={PARKING_AREA.y + 30}
                        fontSize="18"
                        fill="#475569"
                        fontWeight="700"
                    >
                        Parking Area
                    </text>

                    {/* GRID */}

                    {renderGrid()}


                    {/* SLOTS */}

                    {
                        slots.map((slot) => {

                            const points =
                                slot.polygon_points
                                    .map(
                                        (p) =>
                                            `${p.x},${p.y}`
                                    )
                                    .join(" ");

                            const isSelected =
                                selectedSlotId ===
                                slot.slot_id;

                            return (

                                <g
                                    key={slot.slot_id}
                                    onMouseDown={(e) =>
                                        handleMouseDown(
                                            e,
                                            slot
                                        )
                                    }
                                    onClick={() =>
                                        handleSelectSlot(
                                            slot.slot_id
                                        )
                                    }
                                    style={{
                                        cursor: "move"
                                    }}
                                >

                                    <polygon
                                        points={points}
                                        fill={
                                            isSelected
                                                ? "#2563eb"
                                                : "#1f2937"
                                        }
                                        stroke={
                                            isSelected
                                                ? "#60a5fa"
                                                : "#4b5563"
                                        }
                                        strokeWidth="2"
                                    />

                                    <text
                                        x={
                                            slot.slot_center_x
                                        }
                                        y={
                                            slot.slot_center_y + 4
                                        }
                                        textAnchor="middle"
                                        fontSize="12"
                                        fill="white"
                                        fontWeight="600"
                                    >
                                        {
                                            slot.slot_code
                                        }
                                    </text>

                                </g>
                            );
                        })
                    }

                </svg>

            </div>

        </div>
    );
}